import { CalendarIcon } from '@heroicons/react/24/outline'
import { EnvelopeIcon, PhoneIcon, UserIcon } from '@heroicons/react/24/solid'
import { FC } from 'react'

import { useUserContactDetails } from '@/api/models/users'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import CalendarPlusIcon from '@/components/entities/user/assets/CalendarPlus.svg'
import { SetUserProfileRoute } from '@/components/entities/user/hooks/useUserProfileRoute'
import { UserProfileScreens } from '@/components/entities/user/lib/types'
import {
  formatMskDate,
  formatPermissionsExpiresAt
} from '@/components/entities/user/lib/utils'
import { useUserProfileStore } from '@/components/entities/user/model/store'
import {
  PermissionsBlock,
  PermissionsBlockProject
} from '@/components/entities/user/ui/PermissionsBlock'
import UserProfileInfo from '@/components/entities/user/ui/UserProfileInfo'
import { AppButton } from '@/components/shared/button'
import { PERMISSIONS_EXPIRES_AT_LABEL } from '@/components/shared/datepicker/model/permissionsExpiresAt'
import DrawerError from '@/components/shared/drawer/ui/DrawerError'
import { AppInput } from '@/components/shared/input'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'

import CloseContactAccess from './CloseContactAccess'
import UserContactFooter from './UserContactFooter'
import UserContactSkeleton from './UserContactSkeleton'

type Props = {
  contactId: string
  setUserProfileRoute: SetUserProfileRoute
  contentHeight: string
  setFooterRef: (ref: HTMLElement | null) => void
}

const UserContactScreen: FC<Props> = ({
  contactId,
  setUserProfileRoute,
  contentHeight,
  setFooterRef
}) => {
  const { reachGoal } = useYandexMetrika()

  const { contact, isLoading } = useUserContactDetails({ contactId })

  const { isError, setIsError } = useUserProfileStore()

  const onCloseAccessButtonClick = () => {
    setUserProfileRoute(UserProfileScreens.Contacts)
    reachGoal(YandexMetrikaEvent.CONTACTS_DELETE)
  }

  const infoItems = [
    {
      key: 'name',
      icon: <UserIcon />,
      value: contact?.name
    },
    {
      key: 'phone',
      icon: <PhoneIcon />,
      value: contact?.phone
    },
    {
      key: 'email',
      icon: <EnvelopeIcon />,
      value: contact?.email
    },
    {
      key: 'activatedAt',
      icon: <CalendarPlusIcon />,
      value: (
        <span data-testid="user-contact-activated-at">
          <span className="text-grey-70">Дата активации: </span>
          {formatMskDate(contact?.activatedAt)}
        </span>
      )
    },
    {
      key: 'permissionsExpiresAt',
      icon: <CalendarIcon />,
      value: (
        <span data-testid="user-contact-expires-at">
          <span className="text-grey-70">{PERMISSIONS_EXPIRES_AT_LABEL}: </span>
          {formatPermissionsExpiresAt(contact?.permissionsExpiresAt)}
        </span>
      )
    }
  ]

  const accessProjects: PermissionsBlockProject[] =
    contact?.permissions?.map(({ projectId, projectName, premises }) => ({
      id: projectId,
      name: projectName,
      rows: premises.map(({ premiseName, permissionTypes }) => ({
        id: premiseName,
        premise: premiseName,
        permissions: permissionTypes.join(', ')
      }))
    })) ?? []

  if (isLoading) {
    return (
      <UserContactSkeleton
        contentHeight={contentHeight}
        setFooterRef={setFooterRef}
      />
    )
  }
  if (isError) {
    return (
      <>
        <DrawerError style={{ height: contentHeight }} />
        <UserContactFooter setFooterRef={setFooterRef}>
          <AppButton
            fluid
            size="large"
            className="sm:!h-44"
            onClick={() => setIsError(false)}
          >
            Понятно
          </AppButton>
        </UserContactFooter>
      </>
    )
  }

  return (
    <>
      <div
        className="overflow-y-auto px-40 lg:px-24 sm:px-16"
        style={{ height: contentHeight }}
      >
        <UserProfileInfo infoItems={infoItems} className="mt-16 lg:mt-0" />
        {contact?.customName && (
          <AppInput
            dataTestId="user-contact-description-input-id"
            className="mt-40 !h-56 border border-grey-20 bg-white md:mt-20 xxs:!h-48"
            inputClassName="opacity-50"
            placeholder="Описание контакта"
            disabled
            defaultValue={contact.customName}
          />
        )}
        <PermissionsBlock
          className="my-40 lg:my-20"
          projects={accessProjects}
        />
      </div>

      <UserContactFooter setFooterRef={setFooterRef}>
        <CloseContactAccess
          isRevocable={contact.isRevocable || false}
          contactId={contactId}
          onSuccess={onCloseAccessButtonClick}
          onError={() => setIsError(true)}
        />
      </UserContactFooter>
    </>
  )
}

export default UserContactScreen
